import { getAsyncTokenCookie, getJwtUserId, getTokenCookie, removeTokenCookie, setTokenCookie } from '@root/utils/cookies/cookies';
import { SentryBreadcrumb } from '@root/utils/SentryHelper';
import { NextRequest } from 'next/server';

import { getRuntimeEnv } from '@/_utils/environment';

export class FetcherError extends Error {
  body: any;

  status: number;

  response: Response;

  constructor(message: string, status: number, response: Response, body: string) {
    super(message);
    this.status = status;
    this.response = response;
    try {
      this.body = JSON.parse(body);
    } catch {
      this.body = body;
    }
  }
}

type RefreshTokenData = { access: string; refresh: string };
const refreshPromises: Record<number, Promise<RefreshTokenData>> = {};

export const getRefreshTokenPromise = async (refreshToken: string): Promise<RefreshTokenData> => {
  const userId = getJwtUserId(refreshToken);

  if (userId === null) throw new Error('Could not get refresh token');

  if (refreshPromises[userId]) return refreshPromises[userId];

  const { backendUrl } = getRuntimeEnv();
  const isClient = typeof document !== 'undefined';

  refreshPromises[userId] = fetch(`${backendUrl}/tokens/refresh/`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-product': isClient ? 'browser' : 'nextjs' },
    body: JSON.stringify({ refresh: refreshToken })
  })
    .then(async (response) => {
      if (response.status < 200 || response.status >= 300) throw new Error(response.statusText);

      const { token: access, refresh } = await response.json();

      // Set the cookies
      if (isClient) {
        setTokenCookie({ access, refresh });
      }

      return { access, refresh };
    })
    .finally(() => {
      delete refreshPromises[userId];
    });

  return refreshPromises[userId];
};

const fetchRequest = async (url: string, options: RequestOptions) => {
  const { backendUrl } = getRuntimeEnv();
  const fullUrl = new URL(url.startsWith('http') ? url : `${backendUrl}/${url}`);

  try {
    const headers = new Headers();

    headers.set('X-Product', typeof document === 'undefined' ? 'nextjs' : 'browser');

    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (value) headers.set(key, value);
      });
    }

    if (!options.isPublicRoute) {
      let { token, refresh } = options.nextRequest ? getTokenCookie({ request: options.nextRequest }) : await getAsyncTokenCookie();

      if (!token && refresh) {
        token = (await getRefreshTokenPromise(refresh)).access;
      }

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const requestAttrs: RequestInit = { method: options.method || 'GET', credentials: 'include' };

    if (options.method === 'GET') {
      const entries =
        typeof window !== 'undefined' && (options.params instanceof URLSearchParams || options.params instanceof FormData)
          ? options.params.entries()
          : Object.entries(options.params || {});

      for (const [key, value] of entries) {
        if (value) {
          fullUrl.searchParams.append(key, String(value));
        }
      }
    } else if (options.body instanceof FormData) {
      // Setting "content-type" makes it break. Let `fetch` find out what to do.
      requestAttrs.body = options.body;
    } else {
      requestAttrs.body = JSON.stringify(options.body || {});
      headers.set('Content-Type', 'application/json');
    }

    requestAttrs.headers = Object.fromEntries(headers.entries());

    if (options.signal) {
      requestAttrs.signal = options.signal;
    }

    const response = await fetch(fullUrl.toString(), requestAttrs);

    if (!response.ok) {
      // Handle 401 with token refresh
      if (response.status === 401) {
        const { refresh: refreshToken } = await getAsyncTokenCookie();
        // Try to refresh the token if we have a refresh token

        if (refreshToken) {
          try {
            const { access } = await getRefreshTokenPromise(refreshToken);

            SentryBreadcrumb.info({
              category: 'fetcher',
              message: 'Successfully refreshed token',
              data: { url: fullUrl.toString() }
            });

            // Retry the original request with the new token
            headers.set('Authorization', `Bearer ${access}`);

            const retryResponse = await fetch(fullUrl.toString(), { ...requestAttrs, headers: Object.fromEntries(headers.entries()) });

            if (retryResponse.ok) {
              const body = await retryResponse.text?.();

              try {
                if (retryResponse.status === 204) {
                  return null;
                }
                return options.isTextResponse ? body : JSON.parse(body);
              } catch (error) {
                throw new FetcherError('Failed to parse response', retryResponse.status, retryResponse, body);
              }
            }
          } catch (refreshError) {
            if (typeof document !== 'undefined') {
              removeTokenCookie();
            }
          }
        }
      }

      throw new FetcherError('Failed to fetch data', response.status, response, await response.text());
    }
    if (response.status === 204) {
      return null;
    }
    const body = await response.text?.();

    try {
      return options.isTextResponse ? body : JSON.parse(body);
    } catch (error) {
      throw new FetcherError('Failed to parse response', response.status, response, body);
    }
  } catch (error: any) {
    if (!(error instanceof FetcherError)) {
      throw error;
    }

    const errorDetail = error.body?.detail || null;
    const errorDetailMessage = Array.isArray(errorDetail) ? errorDetail?.[0] : errorDetail;

    throw new FetcherError(errorDetailMessage || error.message, error.status, error.response, error.body);
  }
};

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: Record<string, unknown> | Array<unknown> | string | number | boolean | null | FormData;
  cookie?: string;
  params?: Record<string, string | number | boolean | undefined | null> | URLSearchParams | FormData;
  cache?: RequestCache;
  next?: NextFetchRequestConfig;
  isPublicRoute?: boolean;
  isTextResponse?: boolean;
  nextRequest?: NextRequest;
  signal?: AbortSignal;
};

export type FetcherResponse<T> = T | null;

type Fetcher = {
  get: <T>(url: string, options?: RequestOptions) => Promise<FetcherResponse<T>>;
  post: <T>(url: string, options?: RequestOptions) => Promise<FetcherResponse<T>>;
  put: <T>(url: string, options?: RequestOptions) => Promise<FetcherResponse<T>>;
  patch: <T>(url: string, options?: RequestOptions) => Promise<FetcherResponse<T>>;
  delete: <T>(url: string, options?: RequestOptions) => Promise<FetcherResponse<T>>;
};

export const fetcher: Fetcher = {
  get: (url: string, options?: RequestOptions) => fetchRequest(url, { ...options, method: 'GET' }),
  post: (url: string, options?: RequestOptions) => fetchRequest(url, { ...options, method: 'POST' }),
  put: (url: string, options?: RequestOptions) => fetchRequest(url, { ...options, method: 'PUT' }),
  patch: (url: string, options?: RequestOptions) => fetchRequest(url, { ...options, method: 'PATCH' }),
  delete: (url: string, options?: RequestOptions) => fetchRequest(url, { ...options, method: 'DELETE' })
};
